import Drawer from '@material-ui/core/Drawer';
import {assertUnreachable} from '@wandb/weave/common/util/types';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {Icon} from '@wandb/weave/components/Icon';
import {RunState} from '@wandb/weave/components/RunState';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Timestamp} from '@wandb/weave/components/Timestamp';
import React, {useState} from 'react';

import {
  EventTriggeringConditionType,
  TriggeredActionType,
  TriggerExecution,
  TriggerResultType,
} from '../../../../generated/graphql';
import {createDismissibleBanner} from '../../../../pages/Billing/AccountSettings/WarningBanner/DismissibleBanner';
import {
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../../Banner/Banner.styles';
import {ResizableDrawer} from '../../../Webhooks/ResizableDrawer';
import * as S from '../CreateTriggerDrawer/CreateTriggerDrawerLoaded.styles';
import {Trigger} from '../TriggersTable/utilTypes';
import {TriggerDetailsAccordion} from './TriggerDetailsAccordion';
import {isSameTrigger} from './utilData';
import {
  ParsedExecutionAction,
  ParsedExecutionEvent,
  ParsedExecutionResult,
} from './utilTypes';

type ViewTriggerExecutionDrawerProps = {
  execution?: TriggerExecution; // Undefined if no execution is selected
  currentTrigger?: Trigger; // May be undefined if trigger has been deleted

  open: boolean;
  closeDrawer: () => void;
};

export const ViewTriggerExecutionDrawer = ({
  execution,
  currentTrigger,
  open,
  closeDrawer,
}: ViewTriggerExecutionDrawerProps) => {
  const [width, setWidth] = useState(600);
  const onSetWidth = (newWidth: number) => setWidth(newWidth);

  if (!execution) {
    return <></>;
  }

  const triggerWasDeleted = currentTrigger == null;
  const triggerWasEdited =
    currentTrigger && !isSameTrigger(execution, currentTrigger);

  // Parse/cast the optional JSON event/action/result blobs so that we can
  // display human-readable info and key/object pairs where appropriate.
  const execEvent =
    execution.event && (execution.event as ParsedExecutionEvent);
  const execAction =
    execution.action && (execution.action as ParsedExecutionAction);
  const execResult =
    execution.result && (execution.result as ParsedExecutionResult);

  return (
    <Drawer anchor="right" open={open} onClose={closeDrawer}>
      <ResizableDrawer
        startWidth={width}
        minWidth={500}
        onSetWidth={onSetWidth}>
        <Tailwind style={{width: '100%', height: '100%'}}>
          <div className="h-full w-full px-24 pb-20 pt-80">
            <div className="flex h-full w-full flex-col">
              {/* Header */}
              <div className="mb-20 flex items-center">
                <div className="text-xl font-semibold leading-8">
                  Automation execution details
                </div>
                <div className="flex-1 text-right">
                  <Button
                    onClick={closeDrawer}
                    icon="close"
                    size="large"
                    variant="ghost"
                  />
                </div>
              </div>

              {/* Scrollable content */}
              <div className="overflow-y w-full flex-1">
                {/* # Overview */}
                <TriggerDetailsAccordion title="Overview" initialOpen={true}>
                  {/* Warning banner for changed/deleted automations */}
                  {(triggerWasEdited || triggerWasDeleted) && (
                    <TriggerUpdatedOrDeletedBanner
                      updatedOrDeleted={
                        triggerWasDeleted ? 'deleted' : 'updated'
                      }
                    />
                  )}

                  <div className="flex flex-col gap-12 text-base leading-6">
                    <LabeledValue
                      label="Automation"
                      value={execution.triggerName}
                    />
                    <LabeledValue
                      label="Triggered"
                      value={
                        <Timestamp
                          value={execution.createdAt}
                          format="relative"
                        />
                      }
                    />
                    <LabeledValue
                      label="Scope"
                      value={execution.scope?.name ?? 'Unknown'} // Expect a project or artifact collection name
                    />
                    <LabeledValue
                      label="Status"
                      value={<RunState value={execution.state} />}
                    />
                  </div>
                </TriggerDetailsAccordion>

                {/* # Event details */}
                <TriggerDetailsAccordion
                  title="Event details"
                  initialOpen={false}>
                  {execEvent && (
                    <div>
                      <div className="mb-20">
                        <SummarizedExecutionEvent {...execEvent} />
                      </div>
                      <EventDetailsSection {...execEvent} />
                    </div>
                  )}
                </TriggerDetailsAccordion>

                {/* # Action details */}
                <TriggerDetailsAccordion
                  title="Action details"
                  initialOpen={false}>
                  {execAction && (
                    <div>
                      <div className="mb-20">
                        <SummarizedExecutionAction {...execAction} />
                      </div>
                      <ActionDetailsSection {...execAction} />
                    </div>
                  )}
                </TriggerDetailsAccordion>

                {/* # Result details */}
                <TriggerDetailsAccordion
                  title="Result details"
                  initialOpen={false}>
                  {execResult && (
                    <div>
                      <div className="mb-20">
                        <SummarizedExecutionResult {...execResult} />
                      </div>
                      <ResultDetailsSection {...execResult} />
                    </div>
                  )}
                </TriggerDetailsAccordion>
              </div>

              {/* Divider */}
              <div className="-mx-20 h-1 bg-moon-250" />

              {/* Footer */}
              <div className="flex items-center pt-20">
                <Button
                  variant="primary"
                  size="large"
                  className="w-full"
                  onClick={closeDrawer}>
                  Done
                </Button>
              </div>
            </div>
          </div>
        </Tailwind>
      </ResizableDrawer>
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// Helpers/components
// ---------------------------------------------------------------------------

type TriggerUpdatedOrDeletedBannerProps = {
  updatedOrDeleted: 'updated' | 'deleted';
};

const TriggerUpdatedOrDeletedBanner =
  createDismissibleBanner<TriggerUpdatedOrDeletedBannerProps>({
    id: ({updatedOrDeleted}) =>
      `trigger-updated-or-deleted:${updatedOrDeleted}`,
    Component: ({updatedOrDeleted}) => {
      return (
        <Tailwind>
          <S.BannerWithMargin variant="announcement">
            <BannerFlexWrapper>
              <Icon name="info" />
              <BannerTextWrapper>
                <div>
                  <span className="font-semibold">
                    This automation has been {updatedOrDeleted} since it was
                    executed.
                  </span>{' '}
                  Execution details reflect the automation at the time of
                  execution.
                </div>
              </BannerTextWrapper>
            </BannerFlexWrapper>
          </S.BannerWithMargin>
        </Tailwind>
      );
    },
  });

type LabeledValue = {
  label: string;
  value: React.ReactNode;
};

/**
 * A row displaying an event/action/result property as an inline key-value pair.
 */
const LabeledValue = ({label, value}: LabeledValue) => (
  <div className="flex w-full justify-between">
    <div className="mb-8 w-1/3 text-left text-base font-semibold leading-6 text-moon-450">
      {label}
    </div>
    <div className="text-gray-550 -mr-12 w-2/3 text-left text-base leading-6">
      {value}
    </div>
  </div>
);

/**
 * A row displaying an event/action/result property as a preformatted block of
 * text.
 */
const LabeledPreBlock = ({label, text}: {label: string; text: string}) => (
  <div className="w-full">
    <div className="mb-8 w-full text-left text-base font-semibold leading-6 text-moon-450">
      {label}
    </div>
    <div className="mb-8">
      <pre className="overflow-auto rounded bg-moon-100 p-12 text-xs leading-5 text-moon-550">
        {text}
      </pre>
    </div>
  </div>
);

type EventDetailsSectionProps = ParsedExecutionEvent;

const EventDetailsSection = ({eventType, data}: EventDetailsSectionProps) => {
  switch (eventType) {
    case EventTriggeringConditionType.CreateArtifact:
    case EventTriggeringConditionType.LinkModel: {
      return (
        <div>
          <LabeledValue
            label="Artifact collection"
            value={data.artifactCollectionName}
          />
          <LabeledValue
            label="Version"
            value={
              data.artifactVersionIndex != null
                ? `v${data.artifactVersionIndex}`
                : ''
            }
          />
          <LabeledValue label="Project" value={data.projectName} />
        </div>
      );
    }

    case EventTriggeringConditionType.AddArtifactAlias:
    case EventTriggeringConditionType.UpdateArtifactAlias: {
      return (
        <div>
          <LabeledValue label="Alias" value={data.alias} />
          <LabeledValue
            label="Artifact collection"
            value={data.artifactCollectionName}
          />
          <LabeledValue label="Project" value={data.projectName} />
        </div>
      );
    }

    case EventTriggeringConditionType.AddArtifactTag: {
      return (
        <div>
          <LabeledValue label="Tag" value={data.tag} />
          <LabeledValue
            label="Artifact collection"
            value={data.artifactCollectionName}
          />
          <LabeledValue label="Project" value={data.projectName} />
        </div>
      );
    }
    case EventTriggeringConditionType.RunMetric:
    case EventTriggeringConditionType.RunMetricChange: {
      return (
        <div>
          <LabeledValue label="Metric" value={data.metricName} />
          <LabeledValue
            label="Values"
            value={data.metricValues?.join(', ') ?? ''}
          />
          <LabeledValue label="Run" value={data.runName} />
          <LabeledValue label="Project" value={data.projectName} />
        </div>
      );
    }
    case EventTriggeringConditionType.RunState: {
      return (
        <div>
          <LabeledValue label="State" value={data.newRunState} />
          <LabeledValue label="Run" value={data.runName} />
          <LabeledValue label="Project" value={data.projectName} />
        </div>
      );
    }
    default:
      assertUnreachable(eventType);
  }
};

type ActionDetailsSectionProps = ParsedExecutionAction;

const ActionDetailsSection = ({
  actionType,
  data,
}: ActionDetailsSectionProps) => {
  if (!data) {
    return <></>;
  }

  switch (actionType) {
    case TriggeredActionType.QueueJob: {
      return <></>;
    }
    case TriggeredActionType.Notification: {
      return (
        <div>
          <LabeledValue
            label="Channel"
            value={
              data.integration?.channelName
                ? `#${data.integration.channelName}`
                : ''
            }
          />
          <LabeledValue label="Severity" value={data.severity ?? ''} />
          <LabeledPreBlock label="Title" text={data.title ?? ''} />
          <LabeledPreBlock label="Text" text={data.text ?? ''} />
        </div>
      );
    }
    case TriggeredActionType.GenericWebhook: {
      return (
        <div>
          <LabeledPreBlock
            label="URL"
            text={data.integration?.urlEndpoint ?? ''}
          />
          <LabeledPreBlock
            label="Request body"
            text={data.requestPayload ?? ''}
          />
        </div>
      );
    }
    case TriggeredActionType.NoOp: {
      return <></>;
    }
    default:
      assertUnreachable(actionType);
  }
};

type ResultDetailsSectionProps = ParsedExecutionResult;

const ResultDetailsSection = ({
  resultType,
  data,
}: ResultDetailsSectionProps) => {
  if (!data) {
    return <></>;
  }

  // Error content is displayed only if a recorded error is present.
  const errorContent = data.error ? (
    <LabeledValue label="Error" value={data.error.message} />
  ) : null;

  switch (resultType) {
    case TriggerResultType.Default: {
      return <div>{errorContent}</div>;
    }
    case TriggerResultType.QueueJob: {
      // Note: As Launch is still semi-deprecated at the time of MVP,
      // details here are deliberately minimal.  Consider adding more informative
      // details in the future.
      return <div>{errorContent}</div>;
    }
    case TriggerResultType.HttpResponse: {
      return (
        <div>
          {errorContent}
          <LabeledValue label="Status" value={data.status ?? ''} />
          <LabeledValue label="Status code" value={data.statusCode ?? ''} />
          <LabeledPreBlock label="Response body" text={data.body ?? ''} />
        </div>
      );
    }
    default:
      assertUnreachable(resultType);
  }
};

const SummarizedExecutionEvent = ({eventType, data}: ParsedExecutionEvent) => {
  switch (eventType) {
    case EventTriggeringConditionType.CreateArtifact: {
      const collection = data.artifactCollectionName
        ? `"${data.artifactCollectionName}"`
        : '';
      return <span>New artifact version created in {collection}</span>;
    }
    case EventTriggeringConditionType.LinkModel: {
      const collection = data.artifactCollectionName
        ? `"${data.artifactCollectionName}"`
        : '';
      return <span>New artifact version linked to {collection}</span>;
    }
    case EventTriggeringConditionType.AddArtifactAlias: {
      const alias = data.alias ? `"${data.alias}"` : '';
      const collection = data.artifactCollectionName ?? '';
      const versionSuffix =
        data.artifactVersionIndex != null
          ? `:v${data.artifactVersionIndex}`
          : '';
      const versionName = collection ? `"${collection}${versionSuffix}"` : '';
      return (
        <span>
          Alias {alias} added to artifact version {versionName}
        </span>
      );
    }
    case EventTriggeringConditionType.UpdateArtifactAlias: {
      const alias = data.alias ? `"${data.alias}"` : '';
      const collection = data.artifactCollectionName ?? '';
      const versionSuffix =
        data.artifactVersionIndex != null
          ? `:v${data.artifactVersionIndex}`
          : '';
      const versionName = collection ? `"${collection}${versionSuffix}"` : '';
      return (
        <span>
          Alias {alias} updated on artifact version {versionName}
        </span>
      );
    }
    case EventTriggeringConditionType.AddArtifactTag: {
      const tag = data.tag ? `"${data.tag}"` : '';
      const collection = data.artifactCollectionName ?? '';
      const versionSuffix =
        data.artifactVersionIndex != null
          ? `:v${data.artifactVersionIndex}`
          : '';
      const versionName = collection ? `"${collection}${versionSuffix}"` : '';
      return (
        <span>
          Tag {tag} added to artifact version {versionName}
        </span>
      );
    }
    case EventTriggeringConditionType.RunMetric:
    case EventTriggeringConditionType.RunMetricChange: {
      const metric = data.metricName ? `"${data.metricName}"` : '';
      const values = data.metricValues?.join(', ') ?? '';
      const runName = data.runName ? `"${data.runName}"` : '';
      const projectName = data.projectName ? `"${data.projectName}"` : '';
      return (
        <span>
          Run {runName} in project {projectName} logged metric {metric} values:
          {values}
        </span>
      );
    }
    case EventTriggeringConditionType.RunState: {
      const newState = data.newRunState ? `"${data.newRunState}"` : '';
      const runName = data.runName ? `"${data.runName}"` : '';
      const projectName = data.projectName ? `"${data.projectName}"` : '';
      return (
        <span>
          Run {runName} in project {projectName} changed status to {newState}
        </span>
      );
    }
    default:
      assertUnreachable(eventType);
  }
};

const SummarizedExecutionAction = ({
  actionType,
  data,
}: ParsedExecutionAction) => {
  switch (actionType) {
    case TriggeredActionType.Notification: {
      const slackChannel = data?.integration?.channelName;
      return (
        <span>
          Sent Slack notification{' '}
          {slackChannel ? `to channel #${slackChannel}` : ''}
        </span>
      );
    }
    case TriggeredActionType.GenericWebhook: {
      const webhookName = data?.integration?.name
        ? `"${data.integration.name}"`
        : '';
      return <span>Called webhook {webhookName}</span>;
    }
    case TriggeredActionType.QueueJob: {
      return <span>Queued Launch job</span>;
    }
    case TriggeredActionType.NoOp: {
      return <span>No action</span>;
    }
    default:
      assertUnreachable(actionType);
  }
};

const SummarizedExecutionResult = ({
  resultType,
  data,
}: ParsedExecutionResult) => {
  switch (resultType) {
    case TriggerResultType.Default: {
      return (
        <span>
          {data?.error
            ? 'Error during execution'
            : 'Execution completed successfully'}
        </span>
      );
    }
    case TriggerResultType.QueueJob: {
      return <span>Launch job queued</span>;
    }
    case TriggerResultType.HttpResponse: {
      return <span>HTTP response received</span>;
    }
    default:
      assertUnreachable(resultType);
  }
};
